import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaInfoCircle,
  FaHeartbeat,
  FaGlobe,
  FaArrowUp,
  FaSpinner,
  FaPlus,
  FaCalendarAlt,
  FaDownload,
  FaTimes,
  FaExclamationCircle,
  FaClipboardList
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * RouterStatusBadges
 *
 * Replaces the single "Monitoring Active - Router Online" badge in the router
 * detail page header with a rich set of individual status badges:
 *
 * 1. Management IP status badge (up/down with latency)
 * 2. Per-public-IP status badges (up/down with latency)
 * 3. Firmware update badge (when an update is available)
 *
 * Down IPs are clickable and open a modal to create an ad-hoc action item.
 * The firmware badge is clickable and opens the firmware update modal.
 *
 * @param {number} routerId - Router configuration ID
 * @param {object} routerConfig - Router config object (displayName, ipAddress, etc.)
 * @param {object} monitoringStatus - Monitoring status object from the parent page
 * @param {Function} apiFetch - Authenticated fetch from AuthContext
 * @param {Function} onNavigateToActionItem - Callback to navigate to an action item
 */
const RouterStatusBadges = ({ routerId, routerConfig, monitoringStatus, apiFetch, onNavigateToActionItem }) => {
  const [ipStatus, setIpStatus] = useState(null);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [checkingUpdates, setCheckingUpdates] = useState(false);

  // Modal states
  const [actionItemModal, setActionItemModal] = useState(null); // { contextType, affectedIp, affectedIpLabel, title }
  const [firmwareModal, setFirmwareModal] = useState(false);

  // Load public IP status
  const loadIpStatus = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/RouterConfig/${routerId}/public-ip-status`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) setIpStatus(data);
      }
    } catch (catchError) {
        Sentry.captureException(catchError);
      // Non-critical — badges will show as "no data"
    }
  }, [routerId, apiFetch]);

  // Check for firmware updates (on-demand)
  const checkForUpdates = useCallback(async () => {
    setCheckingUpdates(true);
    try {
      const res = await apiFetch(`/api/RouterConfig/${routerId}/check-for-updates`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) setUpdateInfo(data.data);
      }
    } catch (catchError) {
        Sentry.captureException(catchError);
      // Non-critical
    } finally {
      setCheckingUpdates(false);
    }
  }, [routerId, apiFetch]);

  useEffect(() => {
    (async () => { await loadIpStatus(); })();
  }, [loadIpStatus]);

  // Check for updates on load if monitoring is active and router is online
  useEffect(() => {
    if (monitoringStatus?.isMonitoring && monitoringStatus?.isAvailable) {
      (async () => { await checkForUpdates(); })();
    }
  }, [monitoringStatus?.isMonitoring, monitoringStatus?.isAvailable, checkForUpdates]);

  // Handler for creating action items from IP status badges
  const handleIpActionItem = (ip) => {
    setActionItemModal({
      contextType: 'Partial Outage',
      affectedIp: ip.ipAddress || ip.ip,
      affectedIpLabel: ip.label,
      title: `${routerConfig?.displayName} — ${ip.label || ip.ipAddress} unreachable`
    });
  };

  // Handler for ad-hoc action item creation
  const handleAdHocActionItem = () => {
    setActionItemModal({
      contextType: 'General',
      affectedIp: null,
      affectedIpLabel: null,
      title: `${routerConfig?.displayName} — `
    });
  };

  const isMonitoring = monitoringStatus?.isMonitoring;
  const isAvailable = monitoringStatus?.isAvailable;
  const publicIps = ipStatus?.data || [];
  const monitoredPublicIps = publicIps.filter(ip => ip.hasData);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
      {/* Row 1: Management IP + monitoring status */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {isMonitoring ? (
          <StatusBadge
            icon={<FaHeartbeat />}
            label={`Mgmt ${routerConfig?.ipAddress || ''}`}
            status={isAvailable ? 'up' : 'down'}
            detail={isAvailable ? 'Online' : 'Offline'}
            onClick={!isAvailable ? () => setActionItemModal({
              contextType: 'Full Outage',
              affectedIp: routerConfig?.ipAddress,
              affectedIpLabel: 'Management IP',
              title: `${routerConfig?.displayName} — Management IP unreachable`
            }) : undefined}
            tooltip={!isAvailable ? 'Click to create action item' : 'Management IP is responding'}
          />
        ) : (
          <StatusBadge
            icon={<FaInfoCircle />}
            label="Monitoring"
            status="disabled"
            detail="Disabled"
          />
        )}
      </div>

      {/* Row 2: Public IPs */}
      {monitoredPublicIps.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {monitoredPublicIps.map(ip => (
            <StatusBadge
              key={ip.id}
              icon={<FaGlobe />}
              label={ip.label || ip.ipAddress}
              status={ip.isReachable ? 'up' : 'down'}
              detail={ip.isReachable
                ? (ip.roundTripTime != null ? `${ip.roundTripTime}ms` : 'Up')
                : 'Down'}
              onClick={!ip.isReachable ? () => handleIpActionItem(ip) : undefined}
              tooltip={!ip.isReachable
                ? 'Click to create action item for this outage'
                : `${ip.ipAddress} — ${ip.roundTripTime ?? 0}ms`}
            />
          ))}
        </div>
      )}

      {/* Row 3: Firmware update + ad-hoc action item button */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {/* Firmware update badge */}
        {checkingUpdates && (
          <StatusBadge
            icon={<FaSpinner className="spin" />}
            label="Checking updates…"
            status="checking"
          />
        )}
        {!checkingUpdates && updateInfo?.anyUpdateAvailable && (
          <StatusBadge
            icon={<FaArrowUp />}
            label="Update Available"
            status="update"
            detail={updateInfo.latestVersion || updateInfo.upgradeFirmware || ''}
            onClick={() => setFirmwareModal(true)}
            tooltip="Click to view upgrade options"
          />
        )}

        {/* Ad-hoc action item button */}
        <button
          onClick={handleAdHocActionItem}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 10px',
            fontSize: '0.75rem',
            fontWeight: 500,
            background: '#f8f9fa',
            color: '#495057',
            border: '1px solid #dee2e6',
            borderRadius: '14px',
            cursor: 'pointer',
            transition: 'all 0.15s'
          }}
          title="Create an ad-hoc action item linked to this router"
        >
          <FaPlus size={10} /> Action Item
        </button>
      </div>

      {/* Action Item creation modal */}
      {actionItemModal && (
        <CreateRouterActionItemModal
          routerId={routerId}
          routerConfig={routerConfig}
          initialData={actionItemModal}
          apiFetch={apiFetch}
          onClose={() => setActionItemModal(null)}
          onCreated={(actionItemId) => {
            setActionItemModal(null);
            if (onNavigateToActionItem) onNavigateToActionItem(actionItemId);
          }}
        />
      )}

      {/* Firmware update modal */}
      {firmwareModal && updateInfo && (
        <FirmwareUpdateModal
          routerId={routerId}
          routerConfig={routerConfig}
          updateInfo={updateInfo}
          apiFetch={apiFetch}
          onClose={() => setFirmwareModal(false)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// StatusBadge — individual pill-shaped badge for an IP or firmware status
// ─────────────────────────────────────────────────────────────────────────────

const badgeStyles = {
  up: {
    background: '#d4edda',
    border: '1px solid #28a745',
    color: '#155724',
    iconColor: '#28a745'
  },
  down: {
    background: '#f8d7da',
    border: '1px solid #dc3545',
    color: '#721c24',
    iconColor: '#dc3545',
    cursor: 'pointer'
  },
  disabled: {
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    color: '#6c757d',
    iconColor: '#6c757d'
  },
  update: {
    background: '#fff3cd',
    border: '1px solid #ffc107',
    color: '#856404',
    iconColor: '#e6a000',
    cursor: 'pointer'
  },
  checking: {
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    color: '#6c757d',
    iconColor: '#6c757d'
  }
};

const StatusBadge = ({ icon, label, status, detail, onClick, tooltip }) => {
  const style = badgeStyles[status] || badgeStyles.disabled;
  const isClickable = !!onClick;

  return (
    <div
      onClick={onClick}
      title={tooltip}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '4px 10px',
        borderRadius: '14px',
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        transition: 'all 0.15s',
        ...style,
        cursor: isClickable ? 'pointer' : 'default',
        ...(isClickable ? { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' } : {})
      }}
    >
      <span style={{ color: style.iconColor, display: 'inline-flex', fontSize: '0.7rem' }}>{icon}</span>
      <span>{label}</span>
      {detail && (
        <span style={{ fontWeight: 600, marginLeft: '2px' }}>{detail}</span>
      )}
      {isClickable && status === 'down' && (
        <FaExclamationCircle size={10} style={{ marginLeft: '2px', opacity: 0.7 }} />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CreateRouterActionItemModal — modal for creating ad-hoc action items
// ─────────────────────────────────────────────────────────────────────────────

const CONTEXT_TYPES = [
  { value: 'Partial Outage', label: 'Partial Outage', icon: <FaExclamationTriangle />, color: '#e6a000' },
  { value: 'Full Outage', label: 'Full Outage', icon: <FaTimesCircle />, color: '#dc3545' },
  { value: 'Config Change', label: 'Config Change Request', icon: <FaClipboardList />, color: '#007bff' },
  { value: 'Replacement', label: 'Router Replacement', icon: <FaExclamationCircle />, color: '#6f42c1' },
  { value: 'Firmware Upgrade', label: 'Firmware Upgrade', icon: <FaArrowUp />, color: '#e6a000' },
  { value: 'General', label: 'General', icon: <FaInfoCircle />, color: '#6c757d' }
];

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

const CreateRouterActionItemModal = ({ routerId, routerConfig, initialData, apiFetch, onClose, onCreated }) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(initialData?.contextType?.includes('Outage') ? 'High' : 'Medium');
  const [contextType, setContextType] = useState(initialData?.contextType || 'General');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Close modal on Escape key press.
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await apiFetch(`/api/RouterConfig/${routerId}/action-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || null,
          priority,
          contextType,
          affectedIp: initialData?.affectedIp || null,
          affectedIpLabel: initialData?.affectedIpLabel || null
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to create action item');
      }

      const data = await res.json();
      if (data.success) {
        onCreated(data.data?.actionItemId);
      } else {
        throw new Error(data.error || 'Failed to create action item');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaClipboardList /> Create Action Item
          </h3>
          <button onClick={onClose} style={closeButtonStyle}><FaTimes /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
          {/* Router context */}
          <div style={{ padding: '10px 14px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '16px', fontSize: '0.85rem' }}>
            <strong>Router:</strong> {routerConfig?.displayName} ({routerConfig?.ipAddress})
            {initialData?.affectedIp && (
              <div style={{ marginTop: '4px' }}>
                <strong>Affected IP:</strong> {initialData.affectedIpLabel || initialData.affectedIp}
                {initialData.affectedIpLabel && <span style={{ color: '#6c757d' }}> ({initialData.affectedIp})</span>}
              </div>
            )}
          </div>

          {/* Context type chips */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Category</label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {CONTEXT_TYPES.map(ct => (
                <button
                  key={ct.value}
                  type="button"
                  onClick={() => setContextType(ct.value)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '5px 12px',
                    borderRadius: '14px',
                    fontSize: '0.8rem',
                    border: contextType === ct.value ? `2px solid ${ct.color}` : '1px solid #dee2e6',
                    background: contextType === ct.value ? `${ct.color}15` : 'white',
                    color: contextType === ct.value ? ct.color : '#495057',
                    fontWeight: contextType === ct.value ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  {ct.icon} {ct.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Title *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Brief summary of the issue or request"
              style={inputStyle}
              autoFocus
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Additional context, steps to reproduce, or specific instructions…"
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Priority */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Priority</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              {PRIORITIES.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    border: priority === p ? '2px solid' : '1px solid #dee2e6',
                    borderColor: priority === p ? getPriorityColor(p) : '#dee2e6',
                    background: priority === p ? `${getPriorityColor(p)}15` : 'white',
                    color: priority === p ? getPriorityColor(p) : '#495057',
                    fontWeight: priority === p ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div style={{ padding: '10px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '16px', fontSize: '0.85rem' }}>
              <FaExclamationTriangle style={{ marginRight: '6px' }} />{error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" onClick={onClose} style={cancelButtonStyle}>Cancel</button>
            <button type="submit" disabled={submitting} style={submitButtonStyle}>
              {submitting ? <><FaSpinner className="spin" /> Creating…</> : <><FaPlus /> Create Action Item</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FirmwareUpdateModal — shows update info and provides upgrade/schedule options
// ─────────────────────────────────────────────────────────────────────────────

const FirmwareUpdateModal = ({ routerId, routerConfig, updateInfo, apiFetch, onClose }) => {
  const [scheduling, setScheduling] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const minScheduleDate = useMemo(() => {
    const minimumScheduleDate = new Date(Date.now() + 5 * 60 * 1000);
    const timezoneOffsetInMilliseconds = minimumScheduleDate.getTimezoneOffset() * 60 * 1000;
    return new Date(minimumScheduleDate.getTime() - timezoneOffsetInMilliseconds)
      .toISOString()
      .slice(0, 16);
  }, []);

  // Close modal on Escape key press.
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Determine if this is a combined upgrade (RouterOS + Firmware)
  const upgradeType = updateInfo.routerOsUpdateAvailable && updateInfo.firmwareUpdateAvailable
    ? 'Both'
    : updateInfo.routerOsUpdateAvailable
      ? 'RouterOS'
      : 'Firmware';

  const handleUpgradeNow = async () => {
    if (!confirm(`This will upgrade ${routerConfig?.displayName} now.\n\nRouterOS: ${updateInfo.installedVersion} → ${updateInfo.latestVersion}\n${updateInfo.firmwareUpdateAvailable ? `Firmware: ${updateInfo.currentFirmware} → ${updateInfo.upgradeFirmware}\n` : ''}\nThe router will reboot during the process. Continue?`)) {
      return;
    }

    setUpgrading(true);
    setError(null);

    try {
      // Create a scheduled upgrade for "now" (immediate execution)
      const scheduledTime = new Date(Date.now() + 60000).toISOString(); // 1 minute from now
      const createRes = await apiFetch('/api/RouterConfig/scheduled-upgrades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          routerConfigId: routerId,
          upgradeType,
          scheduledDateTime: scheduledTime,
          targetChannel: updateInfo.channel || 'stable',
          targetVersion: updateInfo.latestVersion,
          useSafeMode: true,
          createBackupBeforeUpgrade: true,
          notes: notes || `Immediate upgrade triggered from Router Detail page — ${updateInfo.installedVersion} → ${updateInfo.latestVersion}`
        })
      });

      if (!createRes.ok) {
        const errData = await createRes.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to schedule upgrade');
      }

      const createData = await createRes.json();
      if (!createData.success) throw new Error(createData.error || 'Failed to schedule upgrade');

      // Execute immediately
      const execRes = await apiFetch(`/api/RouterConfig/scheduled-upgrades/${createData.data.scheduledUpgradeId}/execute`, {
        method: 'POST'
      });

      if (!execRes.ok) {
        const errData = await execRes.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to execute upgrade');
      }

      setResult('Upgrade initiated. The router will reboot to apply changes. Monitor the Operations tab for progress.');
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message);
    } finally {
      setUpgrading(false);
    }
  };

  const handleScheduleUpgrade = async () => {
    if (!scheduleDate) {
      setError('Please select a date and time for the upgrade');
      return;
    }

    setScheduling(true);
    setError(null);

    try {
      const res = await apiFetch('/api/RouterConfig/scheduled-upgrades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          routerConfigId: routerId,
          upgradeType,
          scheduledDateTime: new Date(scheduleDate).toISOString(),
          targetChannel: updateInfo.channel || 'stable',
          targetVersion: updateInfo.latestVersion,
          useSafeMode: true,
          createBackupBeforeUpgrade: true,
          notes: notes || `Scheduled upgrade — ${updateInfo.installedVersion} → ${updateInfo.latestVersion}`
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to schedule upgrade');
      }

      const data = await res.json();
      if (data.success) {
        setResult(`Upgrade scheduled for ${new Date(scheduleDate).toLocaleString()}. A backup will be created before the upgrade begins.`);
      } else {
        throw new Error(data.error || 'Failed to schedule upgrade');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message);
    } finally {
      setScheduling(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
        <div style={{ ...modalHeaderStyle, background: '#fff3cd', borderBottom: '1px solid #ffc107' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#856404' }}>
            <FaArrowUp /> Firmware Update Available
          </h3>
          <button onClick={onClose} style={closeButtonStyle}><FaTimes /></button>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Version comparison */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
            {updateInfo.routerOsUpdateAvailable && (
              <>
                <VersionCard label="RouterOS Current" version={updateInfo.installedVersion} status="current" />
                <FaArrowUp style={{ color: '#e6a000', fontSize: '1.2rem' }} />
                <VersionCard label="RouterOS Latest" version={updateInfo.latestVersion} status="available" />
              </>
            )}
            {updateInfo.firmwareUpdateAvailable && (
              <>
                <VersionCard label="Firmware Current" version={updateInfo.currentFirmware} status="current" />
                <FaArrowUp style={{ color: '#e6a000', fontSize: '1.2rem' }} />
                <VersionCard label="Firmware Latest" version={updateInfo.upgradeFirmware} status="available" />
              </>
            )}
          </div>

          {upgradeType === 'Both' && (
            <div style={{ padding: '10px 14px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '6px', marginBottom: '16px', fontSize: '0.82rem' }}>
              <FaInfoCircle style={{ marginRight: '6px', color: '#1976d2' }} />
              Both RouterOS and RouterBOARD firmware will be upgraded. The router will reboot after RouterOS is installed, then the firmware will be applied.
            </div>
          )}

          {result ? (
            <div style={{ padding: '14px', background: '#d4edda', color: '#155724', borderRadius: '6px', marginBottom: '16px' }}>
              <FaCheckCircle style={{ marginRight: '6px' }} />{result}
            </div>
          ) : (
            <>
              {/* Notes */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Reason for upgrade, change window notes…"
                  rows={2}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>

              {/* Schedule option */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>
                  <FaCalendarAlt style={{ marginRight: '4px' }} />
                  Schedule for later
                </label>
                <input
                  type="datetime-local"
                  value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)}
                  min={minScheduleDate}
                  style={inputStyle}
                />
              </div>

              {error && (
                <div style={{ padding: '10px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '16px', fontSize: '0.85rem' }}>
                  <FaExclamationTriangle style={{ marginRight: '6px' }} />{error}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={onClose} style={cancelButtonStyle}>Cancel</button>
                {scheduleDate ? (
                  <button
                    onClick={handleScheduleUpgrade}
                    disabled={scheduling}
                    style={{ ...submitButtonStyle, background: '#6f42c1' }}
                  >
                    {scheduling
                      ? <><FaSpinner className="spin" /> Scheduling…</>
                      : <><FaCalendarAlt /> Schedule Upgrade</>}
                  </button>
                ) : (
                  <button
                    onClick={handleUpgradeNow}
                    disabled={upgrading}
                    style={{ ...submitButtonStyle, background: '#e6a000', color: '#333' }}
                  >
                    {upgrading
                      ? <><FaSpinner className="spin" /> Upgrading…</>
                      : <><FaDownload /> Upgrade Now</>}
                  </button>
                )}
              </div>
            </>
          )}

          {result && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
              <button onClick={onClose} style={submitButtonStyle}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Small version card used in the firmware modal
const VersionCard = ({ label, version, status }) => (
  <div style={{
    padding: '10px 14px',
    background: status === 'available' ? '#d4edda' : '#f8f9fa',
    border: `1px solid ${status === 'available' ? '#28a745' : '#dee2e6'}`,
    borderRadius: '6px',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '0.7rem', color: '#6c757d', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>
      {label}
    </div>
    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: status === 'available' ? '#28a745' : '#333' }}>
      {version || '—'}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────────────────

function getPriorityColor(priority) {
  switch (priority) {
    case 'Critical': return '#dc3545';
    case 'High': return '#e6a000';
    case 'Medium': return '#007bff';
    case 'Low': return '#28a745';
    default: return '#6c757d';
  }
}

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000
};

const modalStyle = {
  background: 'white',
  borderRadius: '8px',
  maxWidth: '600px',
  width: '95%',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
};

const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa'
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '1.1rem',
  cursor: 'pointer',
  color: '#6c757d',
  padding: '4px'
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontWeight: 600,
  fontSize: '0.82rem',
  color: '#495057'
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #dee2e6',
  borderRadius: '4px',
  fontSize: '0.9rem',
  boxSizing: 'border-box'
};

const cancelButtonStyle = {
  padding: '8px 18px',
  background: '#f8f9fa',
  color: '#495057',
  border: '1px solid #dee2e6',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.85rem'
};

const submitButtonStyle = {
  padding: '8px 18px',
  background: '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px'
};

export default RouterStatusBadges;
